import { useState, useEffect } from 'react'
import { Bell, Send, Loader2, Users, User } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { sendNotification } from '../services/adminService'

interface Notification {
  id: string
  title: string
  body: string
  type: string
  isRead: boolean
  createdAt: string
  user?: { id: string; name: string } | null
}

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  // Send form
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [type, setType] = useState('GENERAL')
  const [targetUserId, setTargetUserId] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get('/notifications')
        const data = res.data?.data ?? res.data
        setNotifications(data?.notifications ?? data ?? [])
      } catch {
        toast.error('Failed to load notifications')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const sanitize = (s: string) => s.replace(/<[^>]*>/g, '').substring(0, 500)

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required')
      return
    }
    setSending(true)
    try {
      const result = await sendNotification({
        title: sanitize(title.trim()),
        body: sanitize(body.trim()),
        type,
        email: sendEmail,
        ...(targetUserId.trim() ? { userId: targetUserId.trim() } : {}),
      })
      const parts = [`in-app: ${result.sent}`]
      if (result.pushDelivered != null) parts.push(`push: ${result.pushDelivered}`)
      if (sendEmail) parts.push(`email: ${result.emailQueued ?? 0}`)
      toast.success(`${targetUserId.trim() ? 'Sent to user' : 'Broadcast sent'} — ${parts.join(', ')}`)
      if (sendEmail && !result.emailQueued) {
        toast('Email requested but 0 queued — check SMTP config and recipients’ email preference.', { icon: '⚠️', duration: 6000 })
      }
      setTitle('')
      setBody('')
      setTargetUserId('')
    } catch (err) {
      console.error('[Notifications] send failed:', err)
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(msg ? `Failed: ${msg}` : 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  function timeAgo(date: string) {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  return (
    <div className="space-y-5">
      {/* Send Notification Form */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Send className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-[#1A1A2E]">Send Notification</h3>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title"
                className="w-full bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] outline-none focus:border-primary-400"
              >
                <option value="GENERAL">General</option>
                <option value="PICKUP">Pickup</option>
                <option value="PAYMENT">Payment</option>
                <option value="SYSTEM">System</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#6B7280] mb-1 block">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification message..."
              rows={3}
              className="w-full bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none focus:border-primary-400 resize-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1A1A2E] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-400"
            />
            Also send by email <span className="text-[#9CA3AF] text-xs">(to recipients who allow email notifications)</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">
                User ID <span className="text-[#9CA3AF]">(leave empty for broadcast)</span>
              </label>
              <input
                type="text"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="Optional: target user ID"
                className="w-full bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none focus:border-primary-400"
              />
            </div>
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-5 py-2.5 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {targetUserId.trim() ? 'Send to User' : 'Broadcast'}
            </button>
          </div>
        </div>
      </div>

      {/* Notification History */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#6B7280]" />
          <p className="text-sm font-semibold text-[#1A1A2E]">Recent Notifications</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notifications.map((n) => (
              <div key={n.id} className={`px-5 py-3.5 flex items-start gap-3 ${!n.isRead ? 'bg-primary-50/30' : ''}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  n.user ? 'bg-blue-50' : 'bg-purple-50'
                }`}>
                  {n.user ? (
                    <User className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Users className="w-4 h-4 text-purple-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#1A1A2E] truncate">{n.title}</p>
                    <span className="text-[10px] font-medium text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      {n.type}
                    </span>
                  </div>
                  <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">{timeAgo(n.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
